import { useNavigate, useSearch } from "@tanstack/react-router";
import { Badge } from "@/deps/ui-library";
import { useJournals } from "@/api/queries";
import type { JournalSummary } from "@/api/types";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { JournalStatusBadge } from "./JournalStatusBadge";

export function JournalsListPage() {
  const search = useSearch({ from: "/accounting/journals" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useJournals(listQueryFor(search));

  const columns: Array<Column<JournalSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "11ch",
      numeric: true,
      render: (row) => row.number ?? "Draft",
    },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "narration",
      header: "Narration",
      render: (row) => (
        <span className="flex flex-wrap items-center gap-1.5">
          {row.narration}
          {row.isAdjusting ? (
            <Badge colorScheme="info" size="sm">
              Adjusting
            </Badge>
          ) : null}
          {row.reversesNextYear ? (
            <Badge colorScheme="default" size="sm">
              Reverses next year
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: "debit", header: "Debit", render: (row) => row.debitAccount ?? "—" },
    { key: "credit", header: "Credit", render: (row) => row.creditAccount ?? "—" },
    {
      key: "status",
      header: "Status",
      width: "12ch",
      render: (row) => <JournalStatusBadge status={row.status} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.amount),
    },
  ];

  return (
    <ListScreen
      route="/accounting/journals"
      title="Journals"
      subtitle={data ? `${data.total} this fiscal year` : "Loading journals"}
      actions={
        <PermittedAction
          variant="secondary"
          leftIcon="Plus"
          permission="accounting.journals.create"
          onClick={() => void navigate({ to: "/accounting/journals/new" })}
        >
          New journal voucher
        </PermittedAction>
      }
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Journal ${row.number ?? "draft"}, ${row.narration}, ${formatAmount(row.amount)}`
      }
      onRowClick={(row) =>
        void navigate({ to: "/accounting/journals/$id", params: { id: row.id } })
      }
      searchPlaceholder="Search number, account or narration"
      statuses={null}
      empty={{
        icon: "BookOpen",
        title: "No journals yet",
        description: "Write a journal voucher for anything no other document records.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No journals match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
